import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const WEBCODECS_SOURCE = 'https://www.w3.org/TR/webcodecs/';

/**
 * WebCodecs hardware-vs-software decode probe (novel_bot_detection §2A).
 *
 * PRIMARY SIGNAL: `VideoDecoder.isConfigSupported({ hardwareAcceleration:
 * 'prefer-hardware' })` for H.264 baseline, VP9 and AV1. That is an explicit
 * query of whether a hardware decode configuration exists on this machine,
 * rather than an inference from how long a decode happened to take.
 * SECONDARY SIGNAL: encode/decode timing of one synthetic 1280x720 frame
 * (NOT 4K — an earlier comment said 4K; the frame has always been 1280x720),
 * plus the output pixel format (hardware paths commonly surface NV12/P010,
 * software defaults to I420).
 *
 * Timing alone is not evidence. VP8 hardware decode is uncommon on desktop, so
 * a "software-ish" VP8 decode on a genuine machine is expected. The finding is
 * therefore only raised when the hardware-acceleration query is conclusive and
 * reports no hardware path for any of the three probed codecs, AND the timing
 * and pixel format corroborate a software path. Its scoreContribution is 0
 * until the thresholds have been validated on real macOS/Windows/Linux GPU
 * hardware.
 *
 * All of this runs against media we generate ourselves, so there is no network
 * request and no third-party content.
 */
export const codecsProbe: Probe = {
  id: 'codecs',
  label: 'WebCodecs decode',
  layer: 'media',
  tier: 'client',
  category: 'hardware',
  description: 'Hardware-decode availability per codec, plus software-decode timing and pixel format',
  budgetMs: 8000,

  async run(): Promise<ProbeResult> {
    const results: Record<string, unknown> = {};
    const findings: Finding[] = [];

    const hasEncoder = typeof VideoEncoder === 'function';
    const hasDecoder = typeof VideoDecoder === 'function';
    const hasVideoFrame = typeof VideoFrame === 'function';

    if (!hasDecoder || !hasVideoFrame) {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'WebCodecs VideoDecoder/VideoFrame unavailable'),
        },
      };
    }

    // --- 1. isConfigSupported matrix (static, cheap) ----------------------
    const configMatrix: Record<string, string> = {};
    const configs: Array<{ key: string; codec: string }> = [
      { key: 'h264-baseline', codec: 'avc1.42E01E' },
      { key: 'h264-high', codec: 'avc1.640028' },
      { key: 'vp8', codec: 'vp8' },
      { key: 'vp9', codec: 'vp09.00.10.08' },
      { key: 'av1', codec: 'av01.0.05M.08' },
    ];
    for (const { key, codec } of configs) {
      try {
        const support = await VideoDecoder.isConfigSupported({
          codec,
          codedWidth: 1280,
          codedHeight: 720,
        });
        configMatrix[key] = support.supported ? 'supported' : 'unsupported';
      } catch (err) {
        configMatrix[key] = `error:${(err as Error)?.name ?? 'unknown'}`;
      }
    }

    // --- 1b. explicit hardware-decode availability ------------------------
    // `isConfigSupported` with hardwareAcceleration: 'prefer-hardware' asks the
    // browser whether a hardware decoder config exists at all — verified in
    // Chromium: on a SwiftShader machine it returns supported:false for every
    // codec while prefer-software returns true. We query the three codecs the
    // review called out (H.264 baseline, VP9, AV1); VP8 is deliberately NOT a
    // hardware-availability input because hardware VP8 decode is uncommon on
    // desktop and its absence would be noise.
    const hardwareMatrix: Record<string, 'hardware' | 'software-only' | 'unsupported' | string> = {};
    for (const { key, codec } of configs.filter((c) => c.key !== 'vp8' && c.key !== 'h264-high')) {
      try {
        const hw = await VideoDecoder.isConfigSupported({
          codec,
          codedWidth: 1280,
          codedHeight: 720,
          hardwareAcceleration: 'prefer-hardware',
        });
        if (hw.supported) {
          hardwareMatrix[key] = 'hardware';
          continue;
        }
        const sw = await VideoDecoder.isConfigSupported({
          codec,
          codedWidth: 1280,
          codedHeight: 720,
          hardwareAcceleration: 'prefer-software',
        });
        hardwareMatrix[key] = sw.supported ? 'software-only' : 'unsupported';
      } catch (err) {
        hardwareMatrix[key] = `error:${(err as Error)?.name ?? 'unknown'}`;
      }
    }
    const hwValues = Object.values(hardwareMatrix);
    const hwProbeConclusive = hwValues.length > 0 && hwValues.every((v) => v !== null && !String(v).startsWith('error:'));
    const anyHardwareDecode = hwValues.some((v) => v === 'hardware');

    // --- 2. the real timing measurement ----------------------------------
    // Encode one I420 frame with VideoEncoder, feed the EncodedVideoChunk
    // straight into VideoDecoder, and measure decode output latency. We use
    // the `avc1.42001E` config because a raw I420 frame needs no encoder when
    // we instead build a VP8 keyframe via VideoEncoder; VP8 is widely
    // supported in software and hardware alike, making it a fair comparison.
    const timing = await measureDecodeLatency();

    results.configMatrix = configMatrix;
    results.hardwareMatrix = hardwareMatrix;
    results.decodeLatencyMs = timing.latencyMs;
    results.decodeOk = timing.ok;
    results.outputFormat = timing.format;
    results.decodeError = timing.error;

    // --- 3. classify ------------------------------------------------------
    // Timing thresholds are indicative only (the doc's 1-3 ms HW vs >15 ms SW
    // split). A heavily loaded machine can exceed 15 ms, and a low-power GPU
    // can be under 3 ms, so timing is a SECONDARY signal now. Unvalidated on
    // real GPU hardware, so the finding below contributes no score.
    let classification: 'hardware-ish' | 'software-ish' | 'indeterminate' = 'indeterminate';
    if (timing.ok && timing.latencyMs != null) {
      if (timing.latencyMs < 5) classification = 'hardware-ish';
      else if (timing.latencyMs > 15) classification = 'software-ish';
    }

    results.classification = classification;

    const softwareSignature = classification === 'software-ish' && timing.format === 'I420';
    // The explicit hardware query must be conclusive AND find no hardware path
    // for H.264 baseline, VP9 or AV1. VP8 timing alone never raises this.
    const softwareOnlyHardware = hwProbeConclusive && !anyHardwareDecode;

    if (softwareSignature && softwareOnlyHardware) {
      findings.push({
        id: 'codecs.software-decode',
        severity: 'medium',
        category: 'hardware',
        layer: 'media',
        confidence: 'low',
        title: 'Video decoding appears to be software-only',
        detail:
          'VideoDecoder.isConfigSupported reported no hardware-decode configuration for H.264 baseline, VP9 or AV1, and the timed decode returned an I420 frame at software-like latency. That combination is the signature of a containerized or headless environment. It can also occur on a busy or GPU-less machine, and the thresholds have not been validated against real macOS/Windows/Linux GPU hardware yet, so it is reported at low confidence and contributes no score.',
        sources: [WEBCODECS_SOURCE],
        evidence: [
          `hardwareMatrix=${JSON.stringify(hardwareMatrix)}`,
          `latency=${timing.latencyMs}ms`,
          `format=${timing.format}`,
          'classification=software-ish',
        ],
        scoreContribution: 0,
      });
    }

    return {
      signals: {
        supported: ok(true, 'client'),
        hasEncoder: ok(hasEncoder, 'client'),
        hasDecoder: ok(hasDecoder, 'client'),
        hasVideoFrame: ok(hasVideoFrame, 'client'),
        configMatrix: ok(configMatrix, 'client'),
        hardwareDecodeMatrix: ok(hardwareMatrix, 'client'),
        hardwareDecodeConclusive: ok(hwProbeConclusive, 'derived'),
        anyHardwareDecode: ok(anyHardwareDecode, 'derived'),
        decodeOk: ok(timing.ok, 'client'),
        decodeLatencyMs: ok(timing.latencyMs == null ? null : sig(timing.latencyMs, 3), 'client'),
        outputPixelFormat: ok(timing.format, 'client'),
        classification: ok(classification, 'derived'),
        decodeError: ok(timing.error, 'client'),
      },
      findings,
      parts: {
        codecs: {
          configMatrix,
          // Hash the OUTPUT PIXEL FORMAT and the supported-codec matrix, both of
          // which are stable properties of the media pipeline. We deliberately do
          // NOT hash the timing-derived classification: it flips between
          // hardware-ish/indeterminate on ordinary timing jitter, which would
          // make the part unstable between runs in the same browser and break
          // the compare view. The classification is still shown as a signal.
          outputFormat: timing.format,
        },
      },
    };
  },
};

interface DecodeMeasurement {
  ok: boolean;
  latencyMs: number | null;
  format: string | null;
  error: string | null;
}

async function measureDecodeLatency(): Promise<DecodeMeasurement> {
  // VP8: a keyframe we can encode in-page via VideoEncoder, then decode.
  const width = 1280;
  const height = 720;

  let encoded: EncodedVideoChunk | null = null;
  const encoder = new VideoEncoder({
    output: (chunk) => {
      encoded = chunk;
    },
    error: () => {
      /* recorded via the try/catch below */
    },
  });

  try {
    // Build a simple I420 frame filled with a mid-gray luma so the encoder has
    // real data (a black frame compresses to almost nothing, which would make
    // the decode timing meaningless).
    const ySize = width * height;
    const uvSize = (width / 2) * (height / 2);
    const data = new Uint8Array(ySize + uvSize * 2);
    data.fill(128, 0, ySize); // luma
    data.fill(128, ySize); // chroma

    const frame = new VideoFrame(data, {
      format: 'I420',
      codedWidth: width,
      codedHeight: height,
      timestamp: 0,
    });

    encoder.configure({ codec: 'vp8', width, height, bitrate: 1_000_000, framerate: 1 });
    encoder.encode(frame, { keyFrame: true });
    frame.close();
    await encoder.flush();
    encoder.close();

    if (!encoded) {
      return { ok: false, latencyMs: null, format: null, error: 'encoder produced no chunk' };
    }

    const chunk = encoded as EncodedVideoChunk;

    // Now time the decode of that single keyframe.
    let outputFormat: string | null = null;
    const decoder = new VideoDecoder({
      output: (out) => {
        outputFormat = out.format ?? null;
        out.close();
      },
      error: () => {
        /* recorded via the surrounding try/catch */
      },
    });

    decoder.configure({ codec: 'vp8', codedWidth: width, codedHeight: height });
    const t0 = performance.now();
    decoder.decode(chunk);
    await decoder.flush();
    const latencyMs = performance.now() - t0;
    decoder.close();

    return { ok: true, latencyMs, format: outputFormat, error: null };
  } catch (err) {
    try {
      if (encoder.state !== 'closed') encoder.close();
    } catch {
      /* ignore */
    }
    return { ok: false, latencyMs: null, format: null, error: String((err as Error)?.message ?? err) };
  }
}

registerProbe(codecsProbe);
